import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface CustomersListProps {
  onEdit: (customer: any) => void;
  onViewDetail: (customer: any) => void;
}

interface Client {
  id: number;
  code: string;
  raison_sociale: string;
  nom_commercial: string | null;
  nif: string | null;
  contact: string | null;
  telephone: string | null;
  email: string | null;
  type_client: 'PARTICULIER' | 'PROFESSIONNEL' | 'GROSSISTE' | 'SEMI_GROSSISTE';
  delai_paiement_jours: number;
  plafond_credit: number;
  encours_actuel: number;
  risque_credit: 'FAIBLE' | 'MOYEN' | 'ELEVE' | 'CRITIQUE';
  actif: boolean;
  bloque: boolean;
  commercial?: { name: string } | null;
}

export default function CustomersList({ onEdit, onViewDetail }: CustomersListProps) {
  const [customers, setCustomers] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // ... le reste du code inchangé
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const response = await getResources('clients');
        if (response.data.success) {
          setCustomers(response.data.data);
        }
      } catch (err: any) {
        toast.error('Erreur lors du chargement des clients');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.raison_sociale.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.code && customer.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.nif && customer.nif.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.telephone && customer.telephone.includes(searchTerm));

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && customer.actif && !customer.bloque) ||
      (statusFilter === 'inactive' && !customer.actif) ||
      (statusFilter === 'suspended' && customer.bloque);

    const matchesCategory = categoryFilter === 'all' || customer.type_client === categoryFilter;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  const getStatusColor = (client: Client) => {
    if (client.bloque) return 'bg-red-100 text-red-800';
    if (!client.actif) return 'bg-gray-100 text-gray-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusLabel = (client: Client) => {
    if (client.bloque) return 'Bloqué';
    if (!client.actif) return 'Inactif';
    return 'Actif';
  };

  const getCategoryLabel = (type: string) => {
    const labels: Record<string, string> = {
      PARTICULIER: 'Particulier',
      PROFESSIONNEL: 'Professionnel',
      GROSSIST  : 'Grossiste',
      SEMI_GROSSISTE: 'Semi-Grossiste'
    };
    return labels[type] || type;
  };

  const getCreditRiskColor = (risque: string) => {
    const colors: Record<string, string> = {
      FAIBLE: 'text-green-600',
      MOYEN: 'text-orange-600',
      ELEVE: 'text-red-600',
      CRITIQUE: 'text-red-800 font-bold'
    };
    return colors[risque] || 'text-gray-600';
  };


  // ✅ DÉCLARÉE EN PREMIER
  const toNumber = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Supprime espaces ou caractères non numériques
      return parseFloat(value.replace(/\s/g, '')) || 0;
    }
    return 0;
  };

  // ✅ Maintenant on peut l'utiliser sans erreur
  const stats = {
    total: customers.length,
    active: customers.filter(c => c.actif && !c.bloque).length,
    inactive: customers.filter(c => !c.actif).length,
    blocked: customers.filter(c => c.bloque).length,
    totalCredit: customers.reduce((sum, c) => sum + toNumber(c.plafond_credit), 0),
    usedCredit: customers.reduce((sum, c) => sum + toNumber(c.encours_actuel), 0),
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Chargement des clients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Total Clients</p>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Clients Actifs</p>
              <p className="text-xl font-bold text-green-600">{stats.active}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-xl text-green-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Crédit Total</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalCredit.toLocaleString()} GNF</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-euro-circle-line text-xl text-purple-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Encours Utilisé</p>
              <p className="text-xl font-bold text-orange-600">{stats.usedCredit.toLocaleString()} GNF</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-wallet-line text-xl text-orange-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                placeholder="Rechercher par nom, code, NIF, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="suspended">Bloqué</option>
            </select>
          </div>

          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              <option value="all">Toutes catégories</option>
              <option value="PARTICULIER">Particulier</option>
              <option value="PROFESSIONNEL">Professionnel</option>
              <option value="GROSSISTE">Grossiste</option>
              <option value="SEMI_GROSSISTE">Semi-Grossiste</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste des clients */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code / Raison Sociale
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  NIF / Contact
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type Client
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commercial
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plafond Crédit
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Encours
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCustomers.map((customer) => {
                const creditUsagePercent = customer.plafond_credit > 0 
                  ? (customer.encours_actuel / customer.plafond_credit) * 100 
                  : 0;
                const availableCredit = customer.plafond_credit - customer.encours_actuel;

                return (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-xs font-medium text-gray-900">{customer.code}</div>
                        <div className="text-sm text-gray-900">{customer.raison_sociale}</div>
                        {customer.nom_commercial && <div className="text-xs text-gray-500">{customer.nom_commercial}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-xs text-gray-900">{customer.nif || '—'}</div>
                        <div className="text-xs text-gray-500">{customer.contact || '—'}</div>
                        <div className="text-xs text-gray-500">{customer.email || '—'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getCategoryLabel(customer.type_client)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                      {customer.commercial?.name || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-xs font-medium text-gray-900">
                          {customer.plafond_credit.toLocaleString()} GNF
                        </div>
                        <div className={`text-xs font-medium ${getCreditRiskColor(customer.risque_credit)}`}>
                          {customer.risque_credit}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-xs font-medium text-gray-900">
                          {customer.encours_actuel.toLocaleString()} GNF
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div
                            className={`h-1.5 rounded-full ${
                              creditUsagePercent > 90 ? 'bg-red-600' :
                              creditUsagePercent > 70 ? 'bg-orange-600' :
                              'bg-green-600'
                            }`}
                            style={{ width: `${Math.min(creditUsagePercent, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Dispo: {availableCredit.toLocaleString()} GNF
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(customer)}`}>
                        {getStatusLabel(customer)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onViewDetail(customer)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded cursor-pointer"
                          title="Voir détails"
                        >
                          <i className="ri-eye-line w-4 h-4"></i>
                        </button>
                        <button
                          onClick={() => onEdit(customer)}
                          className="text-green-600 hover:text-green-900 p-1 rounded cursor-pointer"
                          title="Modifier"
                        >
                          <i className="ri-edit-line w-4 h-4"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredCustomers.length === 0 && (
          <div className="text-center py-8">
            <i className="ri-user-line text-gray-400 text-3xl mb-3"></i>
            <h3 className="text-base font-medium text-gray-900 mb-1">Aucun client trouvé</h3>
            <p className="text-sm text-gray-500">Aucun client ne correspond à vos critères de recherche.</p>
          </div>
        )}
      </div>
    </div>
  );
}